/**
 * WorkBuddy 会话的登录态静默恢复。
 *
 * WorkBuddy 客户端侧的授权关系与 BRM 登录态是两回事：只要用户在客户端仍保持授权，wb.login
 * 就能静默返回授权码，凭它调 binding/login 即可换回 comein 会话并重建 BRM 登录态。因此登录态
 * 失效（本窗口 token 过期、其它窗口退出登录或解除绑定）时不必弹框把用户赶回登录页，本模块
 * 轮询重建登录态：
 *  - 换到登录态 → 记得住来源页就跳回失效前的业务页，否则刷新当前页；
 *  - 取不到授权码（客户端侧已撤销授权）→ 立即转授权页交由用户确认，不再重试；
 *  - 回未绑定 → 绑定关系已被解除，继续轮询等其它窗口完成绑定，超时后转授权页。
 *
 * 三个启动时机：接口返回登录失效码（见两个请求封装的 alertFunc）、其它窗口清掉登录态
 * （workBuddySessionSync）、以及进入登录页——登录页只等用户操作、不会主动发请求，没有
 * 「接口报失效」这个时机，故由本模块的路由钩子在进页面时直接开轮询。
 *
 * 只接管 `openFrom=workbuddy` 会话，普通浏览器维持原有「弹框 + 回登录页」行为。
 */
import { checkLogin, getStorageAuthInfo } from '@/utils/storageOperateApi';
import { isWorkBuddyClient } from '@/utils/embedEntry';
import { exchangeWorkBuddySessionSilently, WORKBUDDY_SESSION_EXCHANGE } from '@/utils/workBuddySilentLogin';
import slsLogger from '@/utils/slsLogger';

/** slsLogger 组件名 */
const LOG_COMPONENT = 'WorkBuddySessionRecovery';
/** WorkBuddy OAuth 独立页面路径前缀：这些页面自身就是登录入口，恢复后需跳回业务页 */
const OAUTH_PATH_PREFIX = '/workbuddy/oauth';
/** WorkBuddy 授权页路由 */
const AUTHORIZE_PATH = '/workbuddy/oauth/authorize';
/** 通用登录页路由 */
const LOGIN_PATH = '/login';
/**
 * 进入即自动开始轮询的页面。
 *
 * 登录页只等用户操作、不会主动发业务请求，因此没有「接口报失效」这个触发时机；用户被踢到
 * 这里后若不轮询，就只能自己重新登录一遍。不含授权页：它有自己的绑定状态轮询，两条链路
 * 同时取一次性授权码会互相抢。
 */
const AUTO_RECOVER_PATHS = new Set([LOGIN_PATH, `${OAUTH_PATH_PREFIX}/login`]);
/** 失效前页面地址的 query 参数名 */
const REDIRECT_URI_QUERY_KEY = 'redirect_uri';
/** 记录上次「恢复成功后刷新」时刻的 sessionStorage key，需跨整页刷新保留且各标签页独立 */
const RELOAD_AT_SESSION_KEY = 'WORKBUDDY_SESSION_RECOVERY_RELOAD_AT';
/**
 * 两次自动刷新之间的冷却时间。
 * 刷新后很快又报登录失效，说明重建登录态解决不了（如账号确实无权限），此时必须交回原有
 * 失效处理，否则会陷入「恢复 → 刷新 → 再失效」的循环刷新。
 */
const RELOAD_COOLDOWN_MS = 30000;
/** 轮询间隔：每轮含一次 wb.login 与一次 binding/login，间隔太短会给客户端与后端造成无谓压力 */
const POLL_INTERVAL_MS = 3000;
/** 等待登录态恢复的上限（2 分钟），需够其它窗口走完解绑 → 重新授权 → 验证码登录 */
const MAX_WAIT_MS = 120000;
/**
 * 刷新前的随机错峰上限。
 * 多个窗口由同一次失效唤起，轮询节奏一致，同时刷新会让各窗口的路由守卫并发唤起 wb.login
 * 重新对齐账号，错开一点更稳。
 */
const RELOAD_JITTER_MS = 1500;

/** 应用路由实例，由 install 注入，避免 utils 直接依赖 router 形成新的循环引用 */
let appRouter = null;
/** 是否已在恢复中：多个失败请求会同时报登录失效，只需一条恢复链路 */
let recovering = false;
/** 当前轮询定时器 */
let pollTimer = 0;
/** 本轮恢复的开始时间，用于判断是否超时 */
let startedAt = 0;
/** 启动恢复时那份已失效登录态的指纹，用于识别其它窗口是否换上了新登录态 */
let staleSessionFingerprint = '';

/**
 * 取当前本地登录态的指纹。
 *
 * 后端判定失效时本地 token 往往还在（如 403），仅凭 checkLogin() 无法区分「登录态已被换成
 * 新的」与「还是那份失效的」，故用 token 本身作为指纹比对。
 * @returns {string} BRM 登录 token，未登录时为空串
 */
function readSessionFingerprint() {
  const authInfo = getStorageAuthInfo() || {};
  return authInfo.brmToken?.access_token || '';
}

/**
 * 距上次恢复后刷新是否还在冷却期内。
 * @returns {boolean} 仍在冷却期内返回 true
 */
function isWithinReloadCooldown() {
  const reloadedAt = Number(window.sessionStorage.getItem(RELOAD_AT_SESSION_KEY) || 0);
  return !!reloadedAt && Date.now() - reloadedAt < RELOAD_COOLDOWN_MS;
}

/**
 * 当前是否停留在登录入口页（通用登录页、WorkBuddy 授权页与 OAuth 登录页）。
 *
 * 这些页面本身就是登录入口：恢复成功后该回到失效前的业务页而不是刷新它们，放弃恢复时也
 * 不必再往授权页跳。
 * @returns {boolean} 位于登录入口页时返回 true
 */
function isOnLoginEntryPage() {
  const path = appRouter?.currentRoute.value.path || '';
  return path === LOGIN_PATH || path.startsWith(OAUTH_PATH_PREFIX);
}

/**
 * 取登录态失效前打开的业务页地址。
 *
 * 登录入口页是失效后才跳过来的，原页面记录在 redirect_uri 上；业务页本身就是原页面。
 * @returns {string} 恢复成功后应回到的地址，直接打开登录入口页时无来源页，返回空串
 */
function resolveOriginPath() {
  const route = appRouter?.currentRoute.value;
  if (!route) return '';
  if (!isOnLoginEntryPage()) return route.fullPath;

  const redirectUri = route.query[REDIRECT_URI_QUERY_KEY];
  const originPath = String(Array.isArray(redirectUri) ? redirectUri[0] || '' : redirectUri || '');
  // `//evil.com` 会被浏览器当成协议相对的外站地址，需与非绝对路径一并排除
  if (!originPath.startsWith('/') || originPath.startsWith('//')) return '';
  return originPath;
}

/**
 * 停止轮询并复位状态。
 * @returns {void}
 */
function stopRecovery() {
  if (pollTimer) window.clearTimeout(pollTimer);
  pollTimer = 0;
  recovering = false;
}

/**
 * 错开一小段随机时间后刷新当前页，并记下刷新时刻用于冷却判断。
 * @returns {void}
 */
function reloadWithJitter() {
  window.sessionStorage.setItem(RELOAD_AT_SESSION_KEY, String(Date.now()));
  window.setTimeout(() => window.location.reload(), Math.random() * RELOAD_JITTER_MS);
}

/**
 * 登录态已恢复：让界面回到失效前的位置。
 *
 * 记得住来源页就直接跳回去；业务页此前已按未登录渲染过（接口报错、列表空态），整页刷新比
 * 逐个组件重试更可靠；直接打开的登录页没有来源页，刷新后由路由守卫按登录态决定去处。
 * @param {string} reason - 恢复来源，仅用于日志
 * @returns {void}
 */
function finishRecovered(reason) {
  const originPath = resolveOriginPath();
  const waitedMs = Date.now() - startedAt;
  stopRecovery();
  slsLogger.console({
    component: LOG_COMPONENT,
    message: 'WorkBuddy 登录态恢复-已恢复登录态',
    extra: { reason, originPath, waitedMs }
  });

  // 登录入口页记着失效前的业务页，直接回去，不必刷新登录页这类中转
  if (originPath && isOnLoginEntryPage()) {
    appRouter.replace(originPath);
    return;
  }
  // 授权页没有来源页时留在本页展示授权结果，避免打断用户正在进行的授权
  if (appRouter?.currentRoute.value.path === AUTHORIZE_PATH) return;
  reloadWithJitter();
}

/**
 * 放弃静默恢复，转授权页由用户确认授权与绑定。
 *
 * 后台窗口立刻跳过去会各自唤起 wb.login 授权弹窗，因此页面不可见时先挂起，等回到前台再跳；
 * 挂起期间登录态可能已被其它窗口恢复，此时按恢复成功处理即可。
 * @param {string} reason - 放弃原因，仅用于日志
 * @returns {void}
 */
function giveUpToAuthorize(reason) {
  const originPath = resolveOriginPath();
  const onLoginEntryPage = isOnLoginEntryPage();
  stopRecovery();
  slsLogger.console({
    component: LOG_COMPONENT,
    message: 'WorkBuddy 登录态恢复-转授权页由用户确认',
    extra: { reason, originPath, onLoginEntryPage }
  });
  // 已经在登录入口页：页面自身就提供登录与授权入口，无需再跳
  if (onLoginEntryPage) return;

  const goAuthorize = () => {
    appRouter.replace({ path: AUTHORIZE_PATH, query: { [REDIRECT_URI_QUERY_KEY]: originPath } });
  };
  if (document.visibilityState === 'visible') {
    goAuthorize();
    return;
  }

  const handleVisibilityChange = () => {
    if (document.visibilityState !== 'visible') return;
    document.removeEventListener('visibilitychange', handleVisibilityChange);
    // 挂起期间其它窗口可能已换上新登录态，此时回到原页面即可，无需再走一遍授权
    if (checkLogin() && readSessionFingerprint() !== staleSessionFingerprint) {
      finishRecovered('挂起期间登录态已恢复');
      return;
    }
    goAuthorize();
  };
  document.addEventListener('visibilitychange', handleVisibilityChange);
}

/**
 * 排下一轮轮询，超时则转授权页。
 * @returns {void}
 */
function scheduleNextRound() {
  if (!recovering) return;
  if (Date.now() - startedAt >= MAX_WAIT_MS) {
    giveUpToAuthorize('等待登录态恢复超时');
    return;
  }
  pollTimer = window.setTimeout(runRecoveryRound, POLL_INTERVAL_MS);
}

/**
 * 单轮恢复：先看登录态是否已被其它窗口换好，再自行取码换取。
 * @returns {Promise<void>}
 */
async function runRecoveryRound() {
  if (!recovering) return;
  // 登录态已换成不同于失效前的那份，说明其它窗口重登成功，本窗口跟着回到原页面即可
  const fingerprint = readSessionFingerprint();
  if (checkLogin() && fingerprint && fingerprint !== staleSessionFingerprint) {
    finishRecovered('其它窗口已恢复登录态');
    return;
  }
  // 后台窗口看不到界面，换回来也没人看，等回到前台再取一次性授权码
  if (document.visibilityState !== 'visible') {
    scheduleNextRound();
    return;
  }

  const result = await exchangeWorkBuddySessionSilently(LOG_COMPONENT);
  // 期间用户可能已自行登录或离开，恢复链路已被停掉
  if (!recovering) return;

  switch (result) {
    case WORKBUDDY_SESSION_EXCHANGE.LOGGED_IN:
      finishRecovered('已静默换取登录态');
      return;
    // 客户端侧未授权、非 WorkBuddy 环境、账号缺机构信息：重试也拿不到登录态
    case WORKBUDDY_SESSION_EXCHANGE.NO_CODE:
    case WORKBUDDY_SESSION_EXCHANGE.NO_JSAPI:
    case WORKBUDDY_SESSION_EXCHANGE.SESSION_INCOMPLETE:
      giveUpToAuthorize(result);
      return;
    // 未绑定 / 请求失败：等其它窗口绑完或网络恢复，继续轮询
    default:
      scheduleNextRound();
  }
}

/**
 * 安装静默恢复能力，需在应用启动时调用一次。
 * @param {import('vue-router').Router} router - 应用路由实例
 * @returns {void}
 * @example
 * installWorkBuddySessionRecovery(router);
 */
export function installWorkBuddySessionRecovery(router) {
  appRouter = router;

  router.afterEach((to) => {
    // 登录态已换成新的一份：无论是本窗口手动登录还是其它窗口重登，都不该再自动换账号
    if (checkLogin() && readSessionFingerprint() !== staleSessionFingerprint) {
      stopWorkBuddySessionRecovery();
      return;
    }
    // 登录页只等用户操作，不会主动发请求触发失效，进页面就开始轮询
    if (AUTO_RECOVER_PATHS.has(to.path)) startWorkBuddySessionRecovery();
  });
}

/**
 * 当前是否已在静默恢复登录态。
 * @returns {boolean} 恢复中返回 true
 */
export function isWorkBuddySessionRecovering() {
  return recovering;
}

/**
 * 启动静默恢复。
 *
 * 幂等：已在恢复中时直接返回已接管，供多个并发失败请求共用同一条链路。
 * @returns {boolean} 是否由本模块接管本次登录失效；返回 false 时调用方应走原有失效处理
 * @example
 * if (startWorkBuddySessionRecovery()) return;
 */
export function startWorkBuddySessionRecovery() {
  // 只接管 WorkBuddy 客户端会话，普通浏览器没有 wb.login 可用
  if (!appRouter || !isWorkBuddyClient()) return false;
  if (recovering) return true;
  // 刚恢复刷新过又立刻失效：静默重建解决不了，交回原有失效处理
  if (isWithinReloadCooldown()) return false;

  recovering = true;
  startedAt = Date.now();
  staleSessionFingerprint = readSessionFingerprint();
  slsLogger.console({
    component: LOG_COMPONENT,
    message: 'WorkBuddy 登录态恢复-开始静默恢复',
    extra: { path: appRouter.currentRoute.value.fullPath }
  });
  runRecoveryRound();
  return true;
}

/**
 * 停止静默恢复，用于用户已自行完成登录等不再需要恢复的场景。
 * @returns {void}
 * @example
 * stopWorkBuddySessionRecovery();
 */
export function stopWorkBuddySessionRecovery() {
  if (!recovering) return;
  slsLogger.console({ component: LOG_COMPONENT, message: 'WorkBuddy 登录态恢复-停止静默恢复' });
  stopRecovery();
}
